<script lang="ts" setup>
export type SpinnerColor =
  | 'neutral'
  | 'primary'
  | 'info'
  | 'warning'
  | 'error'
  | 'on-fill'
  | 'community';

withDefaults(
  defineProps<{
    size?: 'small' | 'medium' | 'large' | 'fill';
    color?: SpinnerColor;
  }>(),
  {
    size: 'medium',
    color: 'neutral',
  },
);
</script>

<template>
  <Transition name="spinner" appear>
    <div
      class="loader"
      :class="size"
      data-testid="loading-spinner"
      :aria-label="$t('components.spinner.altText')"
      aria-busy="true"
    >
      <div
        class="ring"
        :class="{
          'text-accent-neutral': color === 'neutral',
          'text-accent-primary': color === 'primary',
          'text-accent-info': color === 'info',
          'text-accent-warning': color === 'warning',
          'text-accent-error': color === 'error',
          'text-accent-on-fill': color === 'on-fill',
          'text-accent-community': color === 'community',
        }"
      />
    </div>
  </Transition>
</template>

<style scoped>
.loader {
  position: relative;
  display: inline-block;
  color: transparent;
  opacity: 1;
  transition:
    all 0s linear,
    opacity var(--transition-duration-instant) var(--ease-rl-out);
}

.spinner-enter-active {
  transition: opacity var(--transition-duration-base) var(--ease-rl-out);
}

.spinner-enter-from {
  opacity: 0;
}

/* Conic sweep masked to a ring, capped at its brightest end so the head leads */
.ring {
  --ring-thickness: 2px;
  --ring-gap: 210deg;

  position: absolute;
  inset: 0;
  border-radius: var(--radius-half);
  background:
    conic-gradient(from 0deg, transparent 0deg var(--ring-gap), currentcolor 360deg),
    color-mix(in srgb, currentcolor 15%, transparent);
  mask: radial-gradient(
    farthest-side,
    transparent calc(100% - var(--ring-thickness)),
    black calc(100% - var(--ring-thickness))
  );
  animation: spinner-sweep 0.75s cubic-bezier(0.45, 0.2, 0.55, 0.75) infinite;

  &::after {
    position: absolute;
    top: 0;
    left: 50%;
    width: var(--ring-thickness);
    height: var(--ring-thickness);
    margin-left: calc(var(--ring-thickness) / -2);
    content: '';
    background-color: currentcolor;
    border-radius: var(--radius-half);
  }
}

@keyframes spinner-sweep {
  to {
    transform: rotate(360deg);
  }
}

@keyframes spinner-pulse {
  50% {
    opacity: 0.4;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ring {
    background: currentcolor;
    animation: spinner-pulse 1.6s ease-in-out infinite;

    &::after {
      display: none;
    }
  }
}

.small {
  width: 12px;
  height: 12px;
}

.medium {
  width: 16px;
  height: 16px;
}

.large {
  width: 20px;
  height: 20px;
}

.fill {
  width: 100%;
  height: 100%;
}
</style>
